<template>
  <template v-if="!loading">
    <WdsStack gap-custom="20px" class="tw-w-full">
      <WdsStack class="tw-w-3/5" direction-column gap-sm>
        <!-- Classifications List -->
        <div v-if="classification || !loading">
          <!-- Overall Status -->
          <WdsStack direction-column gap-sm>
            <!-- Classification header -->
            <WdsStack
              v-if="classification"
              justify-space-between
              class="tw-bg-true-gray-50 tw-px-4 tw-py-3 tw-border-b"
            >
              <WdsStack>
                <DeterminationLogo determination="" />
              </WdsStack>
              <WdsLabel
                lg
                :grey="classification.status === ClassificationStatus.Incomplete"
                :primary="classification.status === ClassificationStatus.Submitted"
                :warning="classification.status === ClassificationStatus.PendingAcceptance"
                :success="classification.status === ClassificationStatus.Accepted"
                subtle
              >
                {{ overallStatus }}
              </WdsLabel>
            </WdsStack>

            <!-- Classification help text -->
            <WdsStack v-if="classification" class="tw-px-4 tw-py-3">
              {{
                $t(
                  'Worksome Classify helps you correctly classify the worker for this engagement in 3 steps.',
                )
              }}
              {{
                $t(
                  'All questions need to be completed before you can see the classification result and worker can sign the offered contract.',
                )
              }}
            </WdsStack>

            <!-- Updated Hire Alert -->
            <WdsAlert
              v-if="classification?.isReclassificationRequired"
              error
              class="tw-px-4 tw-py-3"
            >
              <WdsStack
                gap-sm
                align-center
                justify-space-between
                class="tw-flex-wrap lg:tw-flex-nowrap"
              >
                <WdsStack gap-sm align-start>
                  <WdsStack align-start>
                    <WdsIcon
                      :name="WdsIconNameEnum.ERROR"
                      :size="20"
                      weight="thin"
                      class="tw-mt-0.5"
                    />
                  </WdsStack>

                  <WdsStack direction-column>
                    <WdsText large>
                      <span class="tw-text-red-600">
                        {{ $t('Re-classification required after contract edits.') }}
                      </span>
                    </WdsText>

                    <WdsText>
                      {{
                        $t('Contract details have changed since last classification was completed.')
                      }}
                      <br />
                      {{ $t('Review and update responses as needed.') }}
                    </WdsText>

                    <WdsText v-if="(result?.hire?.paymentRequests?.data?.length ?? 0) > 0">
                      <span class="tw-text-gray-800">
                        <BaseHtml
                          :html="
                            $t(
                              '<strong>Note:</strong> Worker has already billed on this hire. If the classification result changes, a new hire will be needed for additional payments.',
                            )
                          "
                        />
                      </span>
                    </WdsText>
                  </WdsStack>
                </WdsStack>
                <WdsStack direction-column justify-end align-end class="tw-w-full lg:tw-w-fit">
                  <div>
                    <WdsButton
                      error
                      :href="getEditUrl()"
                      :title="$t('Redo the classification answers')"
                    >
                      {{ $t('Redo') }}
                    </WdsButton>
                  </div>
                </WdsStack>
              </WdsStack>
            </WdsAlert>

            <!-- Classification Steps -->
            <HireClassificationSteps :hire-id="hireId" />

            <!-- Classification Actions -->
            <WdsStack v-if="classification" direction-row gap-sm justify-end class="tw-w-full">
              <WdsButtonGhost
                v-if="hasResult && classification?.pdfUrl"
                size="sm"
                class="tw-place-items-center"
                :href="classification.pdfUrl"
                target="_blank"
              >
                <WdsStack align-center gap-sm>
                  <WdsIcon name="download" :size="16" />
                  {{ $t('Download') }} {{ classification?.wcrTerm }}
                </WdsStack>
              </WdsButtonGhost>

              <template v-if="classification.overridesAnother">
                <div
                  class="tw-self-stretch tw-bg-gray-100 tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5"
                >
                  <div class="tw-w-[1px] tw-self-stretch tw-border-r tw-border-gray-400"></div>
                </div>

                <WdsButtonGhost
                  size="sm"
                  class="tw-place-items-center"
                  @click="showRemoveOverrideModal"
                >
                  <WdsIcon name="close" :size="15" class="tw-mr-1" />
                  <WdsText>
                    {{ $t('Remove override') }}
                  </WdsText>
                </WdsButtonGhost>
              </template>
              <template v-else>
                <div
                  class="tw-self-stretch tw-bg-gray-100 tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5"
                >
                  <div class="tw-w-[1px] tw-self-stretch tw-border-r tw-border-gray-400"></div>
                </div>

                <WdsButtonGhost
                  v-if="canShowOverride"
                  size="sm"
                  class="tw-place-items-center"
                  @click="showOverrideModal"
                >
                  <WdsIcon name="edit-alt" :size="15" class="tw-mr-1" />
                  <WdsText>
                    {{ $t('Override') }}
                  </WdsText>
                </WdsButtonGhost>

                <WdsButtonGhost
                  v-if="redoableClassification"
                  size="sm"
                  class="tw-place-items-center"
                  :href="getEditUrl()"
                >
                  <WdsIcon name="restart" :size="15" class="tw-mr-1" />
                  <WdsText>
                    {{ $t('Redo') }}
                  </WdsText>
                </WdsButtonGhost>
              </template>
            </WdsStack>
          </WdsStack>
        </div>
      </WdsStack>
      <WdsStack class="tw-w-2/5" direction-column gap-lg>
        <!-- Classification History -->
      </WdsStack>

      <!-- Override Modal -->
      <ClassificationOverrideModal
        v-if="classification && showOverride"
        :classification="classification"
        :hire-number="hireNumber"
        :csrf-token="csrfToken"
        @close="resetOverrideModal"
      />

      <!-- Remove Override Confirmation Modal -->
      <ConfirmationModal
        :show="showRemoveOverrideConfirmation"
        :title="$t('You are about to remove the classification override')"
        :description="$t('This will revert to the original classification result.')"
        :cancel-text="$t('Cancel')"
        :confirm-text="$t('Confirm')"
        @cancel="resetRemoveOverrideModal"
        @confirm="handleRemoveOverrideConfirm"
      />
    </WdsStack>
  </template>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import ClassificationOverrideModal from './ClassificationOverrideModal.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { graphql } from '@/gql'
import HireClassificationSteps from '@/components/Hire/HirePageClassification/HireClassificationSteps.vue'
import DeterminationLogo from '@/components/compliances/Determinations/DeterminationLogo.vue'
import { ClassificationResult, ClassificationStatus } from '@/gql/graphql'
import ConfirmationModal from '@/components/Common/ConfirmationModal.vue'
import { getClassificationEditUrl } from '@/utils/classificationUtils'
import BaseHtml from '@/components/BaseHtml.vue'

interface Props {
  hireId: string
}

const props = defineProps<Props>()

const showOverride = ref(false)
const showRemoveOverrideConfirmation = ref(false)

// CSRF Token
const csrfToken = computed(
  () =>
    // @ts-ignore - Laravel CSRF token
    window.Laravel?.csrfToken ||
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ||
    '',
)

// GraphQL Query
const { result, loading } = useQuery(
  graphql(`
    query HireClassification($hire: ID!) {
      hire(id: $hire) {
        id
        number
        paymentRequests {
          data {
            id
          }
        }
        sourceHire {
          id
        }
        job {
          name
        }
        classification {
          id
          status
          result {
            outcome
            label
            title
          }
          title
          wcrTerm
          pdfUrl
          overridesAnother
          overrideReason
          overrideUser {
            id
            name
          }
          disputesAnother
          hasUnknownAnswers
          hasUpdatedAnswers
          hasWorkerCompletedAnswers
          isReclassificationRequired
          hasPendingApproval
          canChange
          canOverride
          type
          complianceName
          acceptedStatus
        }
      }
    }
  `),
  { hire: props.hireId },
  { clientId: 'publicApi' },
)

// Computed Properties
const classification = computed(() => result.value?.hire?.classification ?? null)
const hireNumber = computed(() => result.value?.hire?.number ?? props.hireId)
const hasResult = computed(
  () =>
    classification.value?.result &&
    classification.value?.result?.outcome !== ClassificationResult.Unknown,
)

const overallStatus = computed(() => {
  switch (classification.value?.status) {
    case ClassificationStatus.Submitted:
      return $t('Submitted')
    case ClassificationStatus.PendingAcceptance:
      return $t('Pending acceptance')
    case ClassificationStatus.Accepted:
      return $t('Accepted')
    default:
      return $t('Incomplete')
  }
})

const redoableClassification = computed(
  () =>
    (classification.value?.status === ClassificationStatus.PendingAcceptance ||
      classification.value?.status === ClassificationStatus.Accepted) &&
    classification.value?.canChange,
)

const canShowOverride = computed(() => classification.value?.canOverride)

const getEditUrl = () => {
  if (classification.value?.type) {
    const currentUrl = window.location.href
    return getClassificationEditUrl(classification.value.type, hireNumber.value, currentUrl)
  }
  return '#'
}

const handleRemoveOverrideConfirm = () => {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `/hires/${hireNumber.value}/remove-override-sds`

  const csrfInput = document.createElement('input')
  csrfInput.type = 'hidden'
  csrfInput.name = '_token'
  csrfInput.value = csrfToken.value
  form.appendChild(csrfInput)

  const methodInput = document.createElement('input')
  methodInput.type = 'hidden'
  methodInput.name = '_method'
  methodInput.value = 'DELETE'
  form.appendChild(methodInput)

  document.body.appendChild(form)
  form.submit()
}

const resetRemoveOverrideModal = () => {
  showRemoveOverrideConfirmation.value = false
}

const showOverrideModal = () => {
  showOverride.value = true
}

const resetOverrideModal = () => {
  showOverride.value = false
}

const showRemoveOverrideModal = () => {
  showRemoveOverrideConfirmation.value = true
}
</script>
